import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Upload } from 'lucide-react';
import type { AssetKind } from '@ready/shared';
import { useApi, type ContentOwner, type CourseFile } from '@/lib/api';
import { formatBytes } from '@/lib/files';
import { Badge, Button, ErrorText, Select } from '@/components/ui';
import { useUpload } from './upload';

/** File extensions offered by the upload dialog, per kind. */
export const ACCEPT: Partial<Record<AssetKind | 'any', string>> = {
  video: '.mp4,.mov,.webm,.m4v',
  audio: '.mp3,.m4a,.aac,.wav,.ogg',
  image: '.png,.jpg,.jpeg,.gif,.webp,.svg',
  pdf: '.pdf',
  notebook: '.ipynb',
  any: '',
};
const DOCUMENT_ACCEPT = '.pdf,.ppt,.pptx,.key,.odp,.doc,.docx,.odt,.rtf,.txt,.xls,.xlsx,.csv,.ods';
const CODE_ACCEPT =
  '.py,.js,.ts,.tsx,.jsx,.java,.go,.rs,.c,.cpp,.h,.cs,.rb,.php,.sh,.sql,.yaml,.yml,.json,.toml,.html,.css,.md,.txt';

export const DOCUMENT_KINDS: AssetKind[] = ['pdf', 'slides', 'document', 'spreadsheet'];
export const CODE_KINDS: AssetKind[] = ['code', 'markdown'];

export function acceptFor(kinds: AssetKind[] | 'any'): string {
  if (kinds === 'any') return '';
  if (kinds.some((k) => DOCUMENT_KINDS.includes(k))) return DOCUMENT_ACCEPT;
  if (kinds.some((k) => CODE_KINDS.includes(k))) return CODE_ACCEPT;
  return kinds
    .map((k) => ACCEPT[k])
    .filter(Boolean)
    .join(',');
}

export const STATUS_LABEL: Record<CourseFile['status'], string> = {
  uploading: 'Uploading',
  scanning: 'Checking for viruses…',
  ready: 'Ready',
  infected: 'Removed: unsafe',
  failed: 'Failed',
};

export function useCourseFiles(owner: ContentOwner) {
  const api = useApi();
  return useQuery({
    queryKey: ['courseFiles', owner.kind === 'org' ? owner.orgId : 'library'],
    queryFn: () => api.courseFiles(owner),
  });
}

/** Choose a course file of the given kinds: upload a new one or pick an existing one. */
export function FilePicker({
  owner,
  courseId,
  kinds,
  value,
  onChange,
  label,
}: {
  owner: ContentOwner;
  courseId: string;
  kinds: AssetKind[] | 'any';
  value: string | null;
  onChange: (assetId: string) => void;
  label: string;
}) {
  const files = useCourseFiles(owner);
  const { upload, state } = useUpload(owner, courseId);
  const input = useRef<HTMLInputElement>(null);
  const matching = (files.data?.files ?? []).filter(
    (f) => (kinds === 'any' || kinds.includes(f.kind)) && f.status !== 'failed',
  );
  const current = files.data?.files.find((f) => f.id === value);

  return (
    <div className="space-y-3">
      {current ? (
        <FileSummary file={current} />
      ) : (
        value && (
          <p className="text-sm text-muted-foreground">
            {files.isPending
              ? 'Loading file…'
              : 'Uses a file from the course this was copied from. Upload or choose another to replace it.'}
          </p>
        )
      )}
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={input}
          type="file"
          className="sr-only"
          accept={acceptFor(kinds)}
          aria-label={`Upload ${label}`}
          onChange={async (e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;
            const done = await upload(file);
            if (done) onChange(done.id);
          }}
        />
        <Button
          type="button"
          variant="secondary"
          disabled={state?.phase === 'uploading' || state?.phase === 'checking'}
          onClick={() => input.current?.click()}
        >
          <Upload className="size-4" aria-hidden />
          {value ? `Replace ${label}` : `Upload ${label}`}
        </Button>
        {matching.length > 0 && (
          <Select
            aria-label={`Choose an existing ${label}`}
            className="w-auto min-w-0 flex-1 sm:max-w-xs"
            value=""
            onChange={(e) => e.target.value && onChange(e.target.value)}
          >
            <option value="">Or choose an uploaded file…</option>
            {matching.map((f) => (
              <option key={f.id} value={f.id}>
                {f.filename}
              </option>
            ))}
          </Select>
        )}
      </div>
      {state && state.phase !== 'done' && <UploadProgress state={state} />}
    </div>
  );
}

export function FileSummary({ file }: { file: CourseFile }) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm">
      <span className="min-w-0 flex-1 truncate font-medium">{file.filename}</span>
      <span className="text-muted-foreground">{formatBytes(file.sizeBytes)}</span>
      <Badge>{STATUS_LABEL[file.status]}</Badge>
      {file.previewStatus === 'pending' && <Badge>Preparing preview…</Badge>}
      {file.previewStatus === 'failed' && <Badge>No preview (download only)</Badge>}
    </div>
  );
}

export function UploadProgress({
  state,
}: {
  state: NonNullable<ReturnType<typeof useUpload>['state']>;
}) {
  if (state.phase === 'error') return <ErrorText error={new Error(state.error)} />;
  return (
    <div className="space-y-1 text-sm">
      <div className="flex justify-between gap-3 text-muted-foreground">
        <span className="min-w-0 truncate">{state.filename}</span>
        <span className="shrink-0">
          {state.phase === 'uploading'
            ? `${Math.round(state.progress * 100)}%`
            : 'Checking for viruses…'}
        </span>
      </div>
      <progress
        max={1}
        value={state.phase === 'uploading' ? state.progress : undefined}
        aria-label={`Uploading ${state.filename}`}
        className="h-1.5 w-full appearance-none overflow-hidden rounded-full [&::-moz-progress-bar]:bg-primary [&::-webkit-progress-bar]:bg-muted [&::-webkit-progress-value]:bg-primary"
      />
    </div>
  );
}
